/**
 * THE CAPABILITY CATALOGUE.
 *
 * 04 §7.1 / 02 §5.1 (CF-64): **the code declares which capabilities exist; the table stores only their
 * policy.** Startup validation refuses to boot if the table and this file disagree in either direction —
 * an orphan row and an undeclared capability both fail the boot. Deliberately the same shape as the
 * route registry (G-01/G-02).
 *
 * Grammar (04 §7.1): `<domain>.<object>.<action>` — lower-case, dotted, stable, NEVER reused.
 *
 * PC-2: a capability's namespace binds its context. The gate refuses to evaluate a `platform.*`
 * capability in a TENANT context, and refuses to evaluate any other capability in a PLATFORM context.
 *
 * THE PLATFORM SET IS CLOSED AT 36 — 27 Platform Console (04 §13.2.1) + 9 defined elsewhere (§13.2.2).
 * "There is no 37th, and there is no boolean." Adding one is an architecture change.
 *
 * The TENANT set is open by design: a module declares the capabilities it owns. The baseline gives
 * examples (`fs.workpaper.write`, `je.movein.send`, `vat.filing.submit`, `companies.assignment.manage`,
 * `tenant.user.manage`, `audit.read`, `billing.*`), not an exhaustive list, because the module set is
 * itself migrated over time.
 */
/** 04 §6.2 — the four platform roles. A disjoint namespace from tenant roles. */
export const PLATFORM_ROLES = [
    'platform_owner',
    'platform_operator',
    'platform_support',
    'platform_auditor',
];
/**
 * 01 §A4 — the five tenant role codes.
 *
 * These are NOT seeded by a migration and CANNOT be: `core.role` is tenant-owned (`tenant_id NOT
 * NULL`), so there is no tenant to own them when `0005` runs. They are the codes a TENANT
 * PROVISIONING path will create per tenant — a path that does not exist yet (DEV-0012).
 *
 * The role -> capability grid is deliberately absent: `01` §3.2 and `04` §6.2 give DIFFERENT role
 * lists, and which one is right depends on the Owner's pending clarification about the customer/user
 * model. D5 already constrains whatever it turns out to be: no role may imply a company grant.
 */
export const SEEDED_TENANT_ROLES = [
    'tenant_owner',
    'tenant_admin',
    'tenant_member',
    'tenant_billing_contact',
    'client_viewer',
];
const PO = 'platform_owner';
const POp = 'platform_operator';
const PS = 'platform_support';
const PA = 'platform_auditor';
function platformCap(code, descriptionHe, descriptionEn, requiresStepUp, requiresDualControl, defaultPlatformRoles, isMoneyAffecting = false, 
/** The migration that seeds the row. 5 is the reference-data seed; later ones say so. */
seededIn = 5) {
    return {
        code,
        descriptionHe,
        descriptionEn,
        isPlatform: true,
        owningModule: null,
        requiresStepUp,
        requiresDualControl,
        // PC-1: every scope=PLATFORM declaration is audit: ALWAYS. The capability matches.
        auditLevel: 'always',
        defaultPlatformRoles,
        seededIn,
        ...(isMoneyAffecting ? { isMoneyAffecting: true } : {}),
    };
}
/**
 * WIDEN A SHIPPED CAPABILITY'S ROLE GRID BY A LATER FORWARD-ONLY MIGRATION.
 *
 * The first argument is the capability EXACTLY AS ITS OWN SEEDING MIGRATION WROTE IT, so that
 * migration's generated text is unaffected by anything added here. The second names the roles a
 * later migration grants, and which migration grants each.
 *
 * `defaultPlatformRoles` comes out as the UNION — the one list every reader consults — while
 * `platformRolesGrantedLater` is what `generate-seed.ts` subtracts. Composing it this way means the
 * seed cannot drift by construction rather than by remembering to keep two lists in step: the
 * generator's output is `union MINUS later`, which is the first argument's list, unchanged.
 *
 * A role already in the base grid is REFUSED rather than merged. Listing one here that 0005 already
 * seeded would mean the later migration inserts a duplicate `platform_role_capability` row — and it
 * would silently remove the base grant from the generated seed, i.e. quietly rewrite a shipped
 * migration, which is the exact failure this whole mechanism exists to prevent.
 */
function grantedByLaterMigration(asSeeded, later) {
    for (const { role } of later) {
        if (asSeeded.defaultPlatformRoles.includes(role)) {
            throw new Error(`${asSeeded.code}: '${role}' is already in the seeded grid, so a later migration must not ` +
                `grant it again — that would both duplicate the row and remove the seeded grant from the ` +
                `generated 0005`);
        }
    }
    return {
        ...asSeeded,
        defaultPlatformRoles: [...asSeeded.defaultPlatformRoles, ...later.map((l) => l.role)],
        platformRolesGrantedLater: later,
    };
}
/** 04 §13.2.1 — the 27 Platform Console capabilities, in document order. */
const PLATFORM_CONSOLE_CAPABILITIES = [
    platformCap('platform.tenant.create', 'יצירת לקוח (Tenant)', 'Create a tenant record — the shell only; it grants nothing until activated', 'on_entry', false, [PO, POp]),
    platformCap('platform.tenant.read', 'צפייה בנתוני לקוח', 'View tenant metadata: name, status, plan, counts, contacts. Never business data', 'on_entry', false, [PO, POp, PS, PA]),
    platformCap('platform.tenant.activate', 'הפעלת לקוח', 'Move a tenant from draft/trialing to live', 'on_entry', false, [PO, POp]),
    platformCap('platform.tenant.suspend', 'השעיית לקוח', 'Suspend a tenant', 'per_action', false, [PO, POp]),
    platformCap('platform.tenant.reactivate', 'ביטול השעיה', 'Reverse a suspension', 'per_action', false, [PO, POp]),
    platformCap('platform.tenant.close', 'סגירת לקוח', 'Close/cancel a tenant; starts the retention clock', 'per_action', true, [PO]),
    platformCap('platform.subscription.create', 'יצירת מנוי', 'Create a subscription for a tenant', 'on_entry', false, [PO, POp]),
    platformCap('platform.subscription.edit', 'עריכת תנאי מנוי', 'Edit subscription terms other than money', 'on_entry', false, [PO, POp]),
    platformCap('platform.subscription.plan.assign', 'שיוך תכנית', "Assign or change the tenant's plan", 'on_entry', false, [PO, POp]),
    platformCap('platform.subscription.sku.add', 'הוספת מודול למנוי', 'Add a purchased module/SKU to a subscription', 'on_entry', false, [PO, POp]),
    platformCap('platform.subscription.sku.remove', 'הסרת מודול ממנוי', 'Remove a purchased module/SKU — this removes access', 'per_action', false, [PO, POp]),
    platformCap('platform.subscription.state.change', 'שינוי מצב מנוי', 'Force a subscription state transition outside the normal machine', 'per_action', false, [PO]),
    platformCap('platform.limit.company.set', 'קביעת מגבלת חברות', "Set or override the tenant's company limit", 'on_entry', false, [PO, POp]),
    platformCap('platform.limit.seat.set', 'קביעת מגבלת משתמשים', "Set or override the tenant's seat limit", 'on_entry', false, [PO, POp]),
    platformCap('platform.trial.set', 'קביעת תקופת ניסיון', 'Set trial start and end dates', 'on_entry', false, [PO, POp]),
    platformCap('platform.trial.extend', 'הארכת תקופת ניסיון', 'Extend a running trial', 'on_entry', false, [PO]),
    platformCap('platform.price.set', 'קביעת מחיר', 'Set the commercial price on a subscription', 'per_action', false, [PO], true),
    platformCap('platform.discount.apply', 'מתן הנחה', 'Apply a discount up to a stated ceiling', 'per_action', false, [PO], true),
    platformCap('platform.discount.apply_full', 'מתן הנחה מלאה', 'Apply a discount above that ceiling, up to and including 100%', 'per_action', true, [PO], true),
    platformCap('platform.billing.adjustment.manual', 'התאמה כספית ידנית', 'A manual commercial adjustment to an amount owed', 'per_action', true, [PO], true),
    platformCap('platform.billing.credit.issue', 'הוצאת זיכוי', 'Issue a credit / זיכוי against an invoice', 'per_action', true, [PO], true),
    platformCap('platform.billing.history.read', 'צפייה בהיסטוריית חיוב', "Read a tenant's billing history", 'on_entry', false, [PO, POp, PA]),
    /*
     * ── RUNNING THE BILLING NOW, FOR CHOSEN CUSTOMERS. 0045, 2026-11-02. ─────────────────────
     *
     *     *"בחירת לקוח או קבוצת לקוחות ואישור ביצוע החיוב … החיוב הידני והמתוזמן חייבים
     *     להשתמש באותו מנגנון ולמנוע חיוב כפול."*
     *
     * NOT MONEY-AFFECTING, AND THAT IS A DECISION WITH A REASON. 08 §12B.3 attaches dual control to
     * DISCRETION OVER AN AMOUNT — setting a price (row 1), granting a discount (row 2), a manual
     * adjustment or a credit (row 9). This act chooses no amount: it charges what the price book and
     * the subscription already say, through the same `BillingRunService.run` the timer invokes every
     * night under no authority at all. A second approver would have nothing to approve.
     *
     * WHAT PROTECTS THE CUSTOMER IS NOT AN APPROVER. It is the idempotency key, derived per
     * (subscription, period, settled-failure count) and UNIQUE in the database: the manual trigger
     * and the scheduled one compute the same key, so the second of them is refused by the constraint
     * rather than by a screen.
     *
     * `per_action` BECAUSE IT MOVES MONEY ON A PRESS. The nearest sibling is
     * `platform.release.install` — also an estate operation, also `per_action`, also not
     * money-affecting — and the same reasoning holds: a session open all day re-proves itself.
     */
    platformCap('platform.billing.run.execute', 'ביצוע חיוב', 'Run the billing cycle now for selected customers, through the mechanism the scheduled run uses', 'per_action', false, [PO], false, 45),
    /**
     * ── SENDING A CUSTOMER A COPY OF A DOCUMENT THEY ALREADY HAVE. 0054, 2026-11-04. ─────────
     *
     *     *"ליד כל חשבונית שהופקה … „שליחה חוזרת במייל" … תיעוד מי שלח, מתי, לאיזו כתובת ומה
     *     תוצאת השליחה."*
     *
     * IT IS A SEPARATE ROW FROM `platform.billing.history.read`, AND THAT IS THE WHOLE POINT. The
     * reading and the download run under that capability because they are reads: the operator looks
     * at a document that exists. This one puts a message in somebody's mailbox — an OUTWARD act,
     * with a recipient the operator may choose, which no amount of reading authority covers. The
     * master's §12B.3 does not list it, for 0045's reason: until a person pressed it, resending was
     * nobody's act.
     *
     * NOT MONEY-AFFECTING AND NOT DUAL-CONTROLLED. It chooses no amount, issues no document and
     * changes no figure — the document it sends was issued by the provider and is unaltered. 08
     * §12B.3 attaches dual control to DISCRETION OVER AN AMOUNT, and there is none here.
     *
     * `per_action` STEP-UP ANYWAY, and the reason is the recipient rather than the money: the
     * address is the operator's to override, so the act can direct a customer's tax document to an
     * address of the sender's choosing. A session that has been open all day re-proves itself before
     * doing that, which is the same test `platform.billing.run.execute` applies for a different
     * reason.
     *
     * GRANTED TO THE PLATFORM OWNER ALONE. A second holder is a grant somebody makes on purpose —
     * *"אין להרחיב הרשאות אוטומטית."*
     */
    platformCap('platform.billing.document.resend', 'שליחה חוזרת של מסמך', 'Send an already-issued billing document again, to the billing address or to a one-off address', 'per_action', false, [PO], false, 54),
    /**
     * ── THE SUPPORT QUEUE. 0055, 2026-11-04. ─────────────────────────────────────
     *
     *     *"מנהל התמיכה יוכל לשבץ מטפל ולהעלות לפיתוח. הלוח יציג מספר פנייה, מצב,
     *     מטפל, מועד פתיחה, יעד תגובה והפרות SLA."*
     *
     * READING IS FOUR ROLES AND ACTING IS TWO. The auditor reads everything and changes nothing;
     * the operator administers customers and should see what they are complaining about; naming a
     * handler belongs to the support manager and the owner.
     *
     * NONE IS `per_action` AND NONE IS DUAL-CONTROLLED. 08 §12B.3 attaches those to money and to
     * discretion over an amount. A support ticket moves neither, and a password re-entry to assign
     * one is a control nobody can exercise — a ceremony.
     */
    platformCap('platform.support.ticket.read', 'צפייה בתור התמיכה', 'Read the support queue: tickets, handlers, response targets and SLA breaches', 'on_entry', false, [PO, POp, PS, PA], false, 55),
    platformCap('platform.support.ticket.assign', 'שיבוץ והעלאת פנייה', 'Assign a handler, escalate to development, record a response, close a ticket', 'on_entry', false, [PO, PS], false, 55),
    /*
     * THE RESPONSE TARGET IS THE OWNER'S SETTING AND NOT A NUMBER IN A SERVICE.
     * *"לא רוצה לראות מחיר בקוד אלא תחשיב לפי שדה להזנה שלי"* — an SLA is the same shape of thing as a price,
     * and it is the OWNER'S alone for the same reason a price is.
     */
    platformCap('platform.support.policy.set', 'קביעת יעד התגובה', 'Set the number of hours a first response is due within', 'on_entry', false, [PO], false, 55),
    platformCap('platform.subscription.history.read', 'צפייה בהיסטוריית מנוי', "Read a tenant's subscription state history", 'on_entry', false, [PO, POp, PS, PA]),
    platformCap('platform.entitlement.explain', 'הסבר זכאות', 'Run the entitlement resolver in explain mode and see why it decides as it does', 'on_entry', false, [PO, POp, PS, PA]),
    platformCap('platform.audit.billing.read', 'צפייה ביומן ביקורת מסחרי', 'Read platform billing/commercial audit events', 'on_entry', false, [PO, PA]),
    // 0013. A GENERAL platform audit authority, because the billing one above is a BILLING
    // authority and a route that pointed it at every event in the estate would be widening a
    // capability by choosing where to aim it. Owner and Auditor only: the operator administers
    // customers and support enters their data under grant, and neither job needs the record of
    // everyone else's actions.
    platformCap('platform.audit.read', 'צפייה ביומן ביקורת הפלטפורמה', 'Read platform, security and administrative audit metadata across tenants. Never business data', 'on_entry', false, [PO, PA], false, 13),
    platformCap('platform.catalogue.plan.manage', 'ניהול קטלוג תכניות', 'Create/edit plans in the SaaS product catalogue', 'on_entry', false, [PO]),
    platformCap('platform.catalogue.sku.manage', 'ניהול קטלוג מוצרים', 'Create/edit SKUs and their plan mapping', 'on_entry', false, [PO]),
];
/** 04 §13.2.2 — the 9 platform capabilities defined elsewhere in that document. */
const PLATFORM_OTHER_CAPABILITIES = [
    platformCap('platform.support_grant.request', 'בקשת הרשאת תמיכה', 'Request a support grant into a named tenant', 'per_action', false, [PO, POp, PS]),
    /*
     * "is the dual control" — this capability IS the second pair of eyes; it does not itself require one.
     *
     * ── OWNER DECISION DR-2, OPTION A (2026-10-04): THE OPERATOR MAY APPROVE ──────────────────────
     *
     * `.request` above is held by [Owner, Operator, Support]; `.approve` was held by the OWNER ALONE.
     * One role on the approver side means the requester and the approver are the same person whenever
     * the Owner requests, so the dual control this capability IS could not be exercised at all on a
     * platform with one owner — and a second Platform Owner is NOT permitted merely to make it
     * exercisable (`platform-owner-bootstrap.ts`'s one-time rule stands untouched).
     *
     * The Owner's ruling separates the duties instead: SUPPORT requests, the OPERATOR approves, and
     * the OWNER RETAINS approval authority. The requester may never approve their own request — which
     * is NOT re-implemented here: it is the conditional UPDATE's own `requested_by_user_id != @approver`
     * predicate in `SqlSupportAccessStore.approveSupportGrant`, with `CK_support_grant_dual_control`
     * beneath it and the service's named refusal above it. Widening the grid widens WHO MAY APPROVE
     * SOMEBODY ELSE'S REQUEST, and nothing else.
     *
     * The operator's grant is migration 0018's, not 0005's: 0005 has shipped and is immutable. See
     * `platformRolesGrantedLater` on `CapabilityDefinition` for why that is a field and not an edit.
     */
    grantedByLaterMigration(platformCap('platform.support_grant.approve', 'אישור הרשאת תמיכה', 'Approve a support grant. This capability IS the dual control for §6.4', 'per_action', false, [PO]), [{ role: POp, migration: 18 }]),
    platformCap('platform.support_grant.revoke', 'ביטול הרשאת תמיכה', 'Revoke a support grant from the platform side. The tenant may always revoke from its own console', 'on_entry', false, [PO, POp]),
    platformCap('platform.role.manage', 'ניהול תפקידי פלטפורמה', 'Assign or revoke platform roles', 'per_action', true, [PO]),
    platformCap('platform.user.manage', 'ניהול משתמשי פלטפורמה', 'Platform staff accounts and their lifecycle', 'per_action', false, [PO]),
    // "with the grant" — the dual control is the support grant itself, which §3.7 already requires.
    platformCap('platform.sql.readonly', 'שאילתת קריאה בלבד', 'Break-glass read-only query tool. Requires an active support grant naming the tenant', 'per_action', false, [PO]),
    platformCap('platform.server.restart', 'הפעלה מחדש של השרת', 'Restart the application server', 'per_action', false, [PO, POp]),
    platformCap('platform.secret.rotate', 'החלפת סוד', 'Rotate a secret through the secrets provider', 'per_action', false, [PO, POp]),
    platformCap('platform.principal.inventory.read', 'צפייה במצאי הרשאות SQL', 'Read the SQL principal inventory and its drift report', 'on_entry', false, [PO, POp, PA]),
    /*
     * PLATFORM RELEASE MANAGEMENT. Addendum section 9, seeded by migration 0019.
     *
     * TWO, NOT ONE, and the split is the clause: an operator may INSTALL what has been independently
     * reviewed and published; only the Owner may ACCEPT what was installed as fit to go further.
     * Section 9.1(7) calls it *"a distinct explicit Owner acceptance action"*, and an acceptance an
     * operator could record would not be the Owner's -- while 9.1(8) hangs Production eligibility on
     * exactly that record.
     *
     * `per_action` on both: each is a single consequential act -- replacing the running release, or
     * admitting an artifact to Production -- and `on_entry` would let one re-authentication cover a
     * session's worth of them.
     */
    platformCap('platform.release.install', 'התקנת גרסה שאושרה', 'Authorize and start the installation of a published, reviewed release on this estate', 'per_action', false, [PO, POp], false, 19),
    platformCap('platform.release.accept', 'קבלת גרסה שנבדקה', 'Record the Owner acceptance of a release tested on this estate, which is what makes it eligible for Production', 'per_action', false, [PO], false, 19),
];
export const PLATFORM_CAPABILITIES = [
    ...PLATFORM_CONSOLE_CAPABILITIES,
    ...PLATFORM_OTHER_CAPABILITIES,
];
/** 04 §13.2.3 — the five money-affecting capabilities. Each REQUIRES a governing approval_policy row (AP-2). */
export const MONEY_AFFECTING_CAPABILITIES = PLATFORM_CAPABILITIES.filter((c) => c.isMoneyAffecting === true).map((c) => c.code);
function tenantCap(code, descriptionHe, descriptionEn, owningModule, auditLevel, requiresStepUp = 'none') {
    return {
        code,
        descriptionHe,
        descriptionEn,
        isPlatform: false,
        owningModule,
        requiresStepUp,
        requiresDualControl: false,
        auditLevel,
        defaultPlatformRoles: [],
    };
}
/**
 * TENANT capabilities owned by the Core Foundation modules only.
 *
 * A business module contributes its own when it migrates. Nothing here belongs to FS, CFO, FIN,
 * collection, VAT, bank, CC or JE — none of those has migrated, and declaring their capabilities
 * before their module exists would be exactly the "written, correct, and reaching nothing" pattern
 * G-03 exists to prevent.
 */
export const CORE_TENANT_CAPABILITIES = [
    // ── company_management ────────────────────────────────────────────────────────────────────────
    tenantCap('companies.company.read', 'צפייה בחברות', 'View companies in the tenant', 'company_management', 'never'),
    tenantCap('companies.company.manage', 'ניהול חברות', 'Create and edit company master records', 'company_management', 'on_write'),
    tenantCap('companies.identifier.read', 'צפייה במזהים חיצוניים', 'View a company’s external identifiers and their provenance', 'company_management', 'never'),
    tenantCap('companies.identifier.manage', 'ניהול מזהים חיצוניים', 'Add, supersede and confirm external identifiers on a company', 'company_management', 'on_write'),
    tenantCap('companies.conflict.read', 'צפייה בסתירות זהות', 'View unresolved company identity conflicts', 'company_management', 'never'),
    tenantCap('companies.conflict.resolve', 'הכרעה בסתירת זהות', 'Supply the resolving fact that settles an identity conflict', 'company_management', 'always'),
    tenantCap('companies.group.manage', 'ניהול קבוצות חברות', 'Manage company groups and their membership', 'company_management', 'on_write'),
    // 04 §7.1 names this one verbatim. Step-up required: 04 §2.3 lists "granting/revoking company access".
    // OA-0001 §3.8 — PORTFOLIO IS NOT SECURITY. This capability manages who a company is presented
    // to first; `companies.assignment.manage` manages who may open it. They are deliberately separate
    // capabilities so that delegating work assignment can never delegate access.
    tenantCap('companies.portfolio.manage', 'ניהול תיקי עבודה', 'Assign responsibility for companies. Changes presentation and workload, NEVER access', 'company_management', 'on_write'),
    tenantCap('companies.company.create', 'הקמת חברה', 'Onboard a new company, within purchased capacity', 'company_management', 'always'),
    tenantCap('companies.assignment.manage', 'ניהול הרשאות לחברות', 'Grant and revoke a user’s access to a company', 'company_management', 'always', 'per_action'),
    // ── tenant administration ─────────────────────────────────────────────────────────────────────
    tenantCap('tenant.user.manage', 'ניהול משתמשים', 'Invite, suspend and disable tenant memberships', 'company_management', 'always', 'per_action'),
    tenantCap('tenant.role.manage', 'ניהול תפקידים', 'Manage tenant roles and their capability sets', 'company_management', 'always', 'per_action'),
    tenantCap('tenant.settings.read', 'צפייה בהגדרות המשרד', 'View tenant settings', 'company_management', 'never'),
    tenantCap('tenant.branding.manage', 'ניהול מיתוג המשרד', 'Manage the office name, logo and letterhead. Configuration only — never an access input', 'company_management', 'on_write'),
    tenantCap('tenant.user.invite', 'הזמנת משתמש', 'Invite a user to the tenant, within purchased seat capacity', 'company_management', 'always', 'per_action'),
    tenantCap('tenant.settings.manage', 'ניהול הגדרות המשרד', 'Change tenant settings', 'company_management', 'on_write'),
    // ── the tasks module — §6, seeded by 0027 ──────────────────────────────────────────
    //
    // FOUR AND NOT TWO, AND THE SPLIT IS THE WHOLE POINT. The Owner, 2026-10-29:
    //
    //     *"יצירת מודול המשימות לא תעניק אוטומטית הרשאות ניהול לכל המשתמשים הקיימים."*
    //
    // A single `tasks.item.manage` would have forced exactly that: a clerk cannot do the work asked
    // of them -- comment on it, mark it done -- without a capability that also lets them create work
    // for everybody else and cancel anything in the office. So ACTING ON WHAT IS YOURS and MANAGING
    // WHAT IS EVERYBODY'S are separate, and `tenant_member` holds only the first.
    //
    // `tasks.item.act` is bounded at RUN TIME and not by the gate -- "assigned to me, or raised by
    // me" is a property of a ROW, and the gate decides on declarations. `services/work-items.ts`
    // enforces it, and refuses by name.
    //
    // OWNED BY `company_management`, the K0 core module, DELIBERATELY: a task list is part of the
    // office's working surface and not something sold. A module of its own would need a SKU, and the
    // entitlement gate would then refuse the tasks module to every customer who had not bought it.
    //
    // ── WRITTEN AS `{ ...tenantCap(…), seededIn: 27 }` AND NOT THROUGH A WRAPPER. ──────────
    //
    // The first draft had a `taskCap()` helper that added `seededIn`. It read better and it HID ALL
    // FOUR FROM A CONTROL: `destinations.test.ts` reconciles every capability the navigation names
    // against this file by scanning for `tenantCap(` / `platformCap(`, and a third spelling is a
    // declaration a scanner cannot see. The test failed with `['tasks.item.read']` unknown, which is
    // exactly right -- so the spelling stays the one the control reads.
    { ...tenantCap('tasks.item.read', 'צפייה במשימות', "View the office's tasks and notices", 'company_management', 'never'), seededIn: 27 },
    { ...tenantCap('tasks.item.act', 'טיפול במשימה שלי', 'Comment on and advance an item assigned to or raised by the holder. Bounded per row at run time', 'company_management', 'on_write'), seededIn: 27 },
    { ...tenantCap('tasks.item.manage', 'ניהול משימות', "Raise items for anybody, assign them, and cancel any item in the office", 'company_management', 'on_write'), seededIn: 27 },
    { ...tenantCap('tasks.rule.manage', 'תחזוקת מועדי דיווח', "Maintain the office's reporting deadlines: period, day and source. Changes what the product reminds everybody of", 'company_management', 'always'), seededIn: 27 },
    // ── audit ─────────────────────────────────────────────────────────────────────────────────────
    // 04 §8.2: readers are the tenant's own tenant_admin/tenant_owner via a TENANT-scoped route.
    tenantCap('audit.read', 'צפייה ביומן ביקורת', "Read this tenant’s own audit events", 'company_management', 'never'),
    tenantCap('security.support_grant.read', 'צפייה בהרשאות תמיכה', 'View support grants into this tenant', 'company_management', 'never'),
    tenantCap('security.support_grant.revoke', 'ביטול הרשאת תמיכה', 'Revoke a support grant into this tenant. Always available to the tenant', 'company_management', 'always'),
    // ── billing (tenant side) — 04 §9 names these verbatim ────────────────────────────────────────
    tenantCap('billing.plan.read', 'צפייה בתכנית', 'View the current plan and purchased SKUs', 'billing', 'never'),
    tenantCap('billing.usage.read', 'צפייה בשימוש', 'View metered usage: billable companies, seats', 'billing', 'never'),
    tenantCap('billing.invoice.read', 'צפייה בחשבוניות', 'View invoices and credit notes', 'billing', 'never'),
    tenantCap('billing.invoice.download', 'הורדת חשבונית', 'Download an invoice document', 'billing', 'on_write'),
    tenantCap('billing.payment_method.update', 'עדכון אמצעי תשלום', 'Update the payment method', 'billing', 'always', 'per_action'),
    tenantCap('billing.profile.update', 'עדכון פרטי חיוב', 'Update the billing profile', 'billing', 'always'),
    tenantCap('billing.change.request', 'בקשת שינוי מסחרי', 'Request a commercial change (custom-term tenants take this path, never self-serve)', 'billing', 'always'),
    tenantCap('billing.subscription.cancel', 'ביטול מנוי', 'Cancel the subscription, effective at period end', 'billing', 'always', 'per_action'),
    /*
     * 0044. THE OFFICE BUYS A MODULE FOR ITSELF — the Owner's fourth tab, *"קונה מיד, בכרטיס שלו"*.
     *
     * SAME AUDIT AND SAME STEP-UP AS CANCELLING, because they are the two commitments an office
     * makes about money and a session open all day should re-prove itself for either. NO DUAL
     * CONTROL: it is the office's own money and its own decision, which is the line that separates
     * it from the credit the platform owner issues.
     *
     * ── `seededIn: 44`, AND THE FULL SUITE IS WHAT SAID SO ────────────────────────────────────
     *
     * Written first as a bare `tenantCap(...)`, which means `seededIn` defaults to 5 — and
     * `generate-seed.ts` then emits this row into 0005, an IMMUTABLE, already-applied migration.
     * `generated-artifacts.test.ts` compares the generated text with the file byte for byte and
     * failed on it; `migration-immutability` pins 0005's SHA-256 and would have failed next. The
     * typecheck, the guardrails, the registry and the suites I had run until then were all green,
     * because none of them regenerates the seed.
     *
     * SPELLED `{ ...tenantCap(…), seededIn: 44 }` AND NOT THROUGH A HELPER, for the reason the four
     * `tasks.*` rows above record: `destinations.test.ts` reconciles the navigation's capabilities
     * against this file by SCANNING for `tenantCap(`, and a third spelling is a declaration a
     * scanner cannot see.
     */
    { ...tenantCap('billing.module.purchase', 'רכישת מודול', 'Purchase a module for this office', 'billing', 'always', 'per_action'), seededIn: 44 },
    /*
     * THE OWNER'S SECOND STAGE, WHICH WAS NAMED FOR AN ACT NOTHING COULD PERFORM. 0046.
     *
     *     *"לאחר 14 יום, הסדרה בלבד"* — and *"עדכון אמצעי תשלום אינו הסדרה."*
     *
     * An office restricted to settling had `billing.payment_method.update` (hold an
     * instrument) and `billing.invoice.read` (see what it owes) and no authority to pay. It
     * waited for the nightly pass. This is the press, and it runs the SAME
     * `BillingRunService.run` with `only` set to that office's own subscription, so the
     * idempotency key is the timer's key and a double charge is refused by a UNIQUE
     * constraint rather than by a screen.
     */
    /*
     * `billing.settlement.pay` WAS HERE, seeded in 46 and REMOVED IN 0053 on 2026-11-03. The
     * Owner: *"הלקוח אינו יכול להפעיל חיוב בכרטיס בעצמו... אכוף את ההפרדה גם בשרת."* The route that
     * served it is deleted and the capability row is deleted; this list describes what exists.
     */
    /**
     * ── THE OFFICE MOVES ITSELF BETWEEN PUBLISHED PLANS. **ITEM 4, 0051, 2026-11-03.** ──────────
     *
     * *"שדרוג/הורדה בשירות עצמי."* The other half of 08 §12B.3 row 17, whose request half is
     * `billing.change.request`: a grandfathered contract takes that path and ONLY that path, and
     * everything else is self-servable.
     *
     * `per_action` BECAUSE IT IS THE OFFICE'S OWN MONEY changing by its own hand, and a session
     * somebody left open is not consent. NO DUAL CONTROL, and the reason is §12B.3's rather than a
     * judgement made here: dual control is attached to DISCRETION OVER AN AMOUNT, and this act has
     * none — the figure is the published price.
     */
    /*
     * 0055. A SIGNED-IN PERSON ASKS US SOMETHING, from the support assistant.
     *
     *     *"באישור תיפתח פנייה אמיתית בתור התמיכה … הגבל את קליטת המחלקה לפניות GLPOINT מזוהות."*
     *
     * EVERY MEMBER, NOT ONLY THE OWNER. The person who meets a problem is whoever is using the
     * screen; a bookkeeper who cannot raise a ticket sends an unidentified e-mail instead, which is
     * exactly what this replaces. `client_viewer` is excluded in the migration's grant: a viewer of
     * one client's data is an outside reader, and a ticket carries the office's name.
     *
     * `company_management` OWNS IT for `tasks.item.manage`'s reason -- it is the Core Foundation
     * module that holds every tenant capability that is not billing. A new `support` module would
     * put a row in the catalogue that plans and entitlements read, so an office that had not bought
     * it could not ask for help.
     */
    { ...tenantCap('support.ticket.open', 'פתיחת פנייה לתמיכה', 'Open a support ticket about this organisation, from the support assistant', 'company_management', 'always'), seededIn: 55 },
    { ...tenantCap('billing.plan.change', 'שינוי תכנית', 'Move this office between PUBLISHED plans, under the same §5.3 rules the platform path applies', 'billing', 'always', 'per_action'), seededIn: 51 },
    tenantCap('billing.export.request', 'בקשת ייצוא נתונים', 'Request a data export package', 'billing', 'always'),
];
/** Every capability the code declares. The boot validator reconciles this against `core.capability`. */
export const ALL_CAPABILITIES = [
    ...PLATFORM_CAPABILITIES,
    ...CORE_TENANT_CAPABILITIES,
];
const CAPABILITY_INDEX = new Map(ALL_CAPABILITIES.map((c) => [c.code, c]));
export function findCapability(code) {
    return CAPABILITY_INDEX.get(code);
}
/**
 * 04 §7.1 gives the grammar as `<domain>.<object>.<action>` — "lower-case, dotted, stable, never
 * reused" — and every example it gives has three parts: `fs.workpaper.write`, `je.movein.generate`,
 * `vat.filing.submit`, `companies.assignment.manage`, `platform.sql.readonly`, `tenant.user.manage`.
 *
 * But §8.2 then names the tenant audit capability **`audit.read`**, which has two. Rather than
 * rename a capability the baseline states verbatim, the pattern accepts two to four segments: the
 * properties that are actually load-bearing are lower-case, dotted, stable and never reused, and
 * `audit.read` satisfies all four. A code that is well formed here still has to EXIST in the
 * catalogue, which is the check that does the real work.
 */
const CAPABILITY_CODE_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*){1,3}$/;
export function isWellFormedCapabilityCode(code) {
    return CAPABILITY_CODE_PATTERN.test(code) && code.length <= 128;
}
/** PC-2 — a `platform.` prefix binds the capability to PLATFORM context and nothing else does. */
export function isPlatformCapabilityCode(code) {
    return code.startsWith('platform.');
}
//# sourceMappingURL=capabilities.js.map